import type {
  ActionFunction,
  LinksFunction,
  LoaderFunction
} from '@remix-run/node';
import { json } from '@remix-run/node';
import { Form, redirect, useActionData, useLoaderData } from '@remix-run/react';
import { useEffect, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { SecondaryButton } from '~/components/atoms/button';
import { FlexColumn } from '~/components/atoms/container';
import { EducateLogo } from '~/components/atoms/educate-logo';
import { FormField } from '~/components/molecules/form-field';
import { getDomainUrl, isExpired } from '~/misc';
import { listUserOrganizations } from '~/prisma.server';
import { getUserSession, sendToken } from '~/session.server';
import { AuthenticationError, login } from '~/services/authentication.server';
import loginStyles from '~/styles/atoms/login.css?url';
import { rudderanalytics } from '~/utils/rudderstack.client';

export type ResponseItem = {
  data?: Record<string, any> | Record<string, any>[] | string[] | string;
  errors?: string[];
  metadata?: Record<string, any>;
};

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: loginStyles }
];

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.clone().formData();
  const email = formData.get('email')?.toString();

  if (!email) {
    return json(
      { errors: ['Please provide a valid email address'] },
      { status: 400 }
    );
  }

  try {
    await login(request, 'magic-link');
  } catch (err) {
    console.error(err);
    // pass AuthenticationError exception message to the frontend
    const error = err instanceof AuthenticationError ?
      err.message : "Oops something we didn't anticipate happened.";

    return json(
      { errors: [error], exception: err },
      { status: 500 }
    );
  }

  return json({
    data: 'ok',
    metadata: {
      context: process.env.NODE_ENV,
      timestamp: new Date().toISOString()
    }
  });
};

export const loader: LoaderFunction = async ({ request }) => {
  const session = await getUserSession(request);
  if (session) {
    throw redirect(`/${session?.account_id}`);
  }

  const toastError = new URL(request.url).searchParams.get('error');

  try {
    // this might redirect when OIDC flows are enabled
    const loginSession = await login(request);
    if (loginSession) {
      const headers = await loginSession.getHeaders();
      return redirect('/', { status: 302, headers });
    }
  } catch (err) {
    console.log(err);
  }

  console.log('/login session not found');
  return json({ toastError });
};

export default function Login() {
  let formRef = useRef<HTMLFormElement>(null);
  let actionData = useActionData<any>();
  let loaderData = useLoaderData<{ toastError: string | null }>();

  useEffect(() => {
    if (loaderData?.toastError) {
      toast.error(loaderData.toastError);
    }
  }, [loaderData?.toastError]);

  useEffect(() => {
    if (actionData?.data === 'ok' && formRef.current) {
      const formData = new FormData(formRef.current);
      const email = formData.get('email') as string;
      const organization = formData.get('organization') as string;

      rudderanalytics.page('UnlockAI', 'Login', {
        email,
        organization
      });

      rudderanalytics.track('Login Success', {
        account_id: organization,
        email
      });

      let message = 'Link sent. Check your inbox/spam folders.';

      if (actionData?.metadata?.context === 'development') {
        message = 'Link sent. Check your console.';
      }

      toast.dismiss();
      toast.success(message);
      formRef.current?.reset();
    }

    if (actionData?.errors?.length) {
      rudderanalytics.track('Login Error', {
        errors: actionData?.errors
      });

      actionData?.errors?.forEach((error: string) => {
        toast.error(error);
      });
    }
  }, [actionData]);

  return (
    <FlexColumn alignment="center" style={{ flex: 1, marginTop: 100 }}>
      <EducateLogo />

      <Form
        method="post"
        replace
        ref={formRef}
        style={{ maxWidth: 500, width: '100%', marginTop: 64 }}
        className="login__form"
      >
        <FormField
          name="email"
          type="email"
          className="login__input"
          placeholder="jeandoe@mail.com"
          defaultValue={actionData?.data?.email}
          disabled={actionData?.data?.memberships?.length > 0}
          style={{ marginTop: 16 }}
        />

        <SecondaryButton
          type="submit"
          style={{ margin: '16px 0', width: '100%' }}
          className="login__btn"
          children="Send me a login link"
        />
        <Toaster />
      </Form>
    </FlexColumn>
  );
}
